import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Alert,
  Icon,
  Field,
  Input,
  Dialog,
} from '@chakra-ui/react';
import { useState } from 'react';
import { FiCreditCard, FiLock, FiShield, FiInfo, FiAlertTriangle } from 'react-icons/fi';
import api from '../services/api';
import { toaster } from './ui/toaster';

interface StripePaymentProps {
  planId: number;
  planName: string;
  planPrice: number;
  planFeatures: string[];
  planTier: string;  // Add this field
  onSuccess: () => void;
  onCancel: () => void;
}

const StripePayment: React.FC<StripePaymentProps> = ({
  planName,
  planPrice,
  planFeatures,
  planTier,  // Add this parameter
  onSuccess,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      // Use the tier directly from the plan
      const response = await api.post(`/api/user/upgrade-subscription?new_tier=${planTier}`);

      console.log('Subscription upgrade response:', response.data);

      toaster.create({
        title: 'Subscription Successful',
        description: `You have been subscribed to ${planName}`,
        type: 'success',
        duration: 5000,
      });
      
      onSuccess();
      setIsOpen(false);
    } catch (error: any) {
      console.error('Subscription upgrade error:', error);
      toaster.create({
        title: 'Subscription Failed',
        description: error.response?.data?.detail || 'There was an error processing your subscription',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Button
        colorScheme="brand"
        onClick={() => setIsOpen(true)}
        size="lg"
      >
        <FiCreditCard /> Subscribe to {planName}
      </Button>

      <Dialog.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)} size="lg">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Subscribe to {planName}</Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body pb={6}>
            <VStack gap={6} align="stretch">
              <Alert.Root status="info">
                <Alert.Indicator>
                  <Icon as={FiInfo} />
                </Alert.Indicator>
                <Alert.Description>
                  {planName} - ${planPrice}/month
                </Alert.Description>
              </Alert.Root>

              <Box>
                <Text fontWeight="bold" mb={3}>Plan Features:</Text>
                <VStack align="start" gap={2}>
                  {planFeatures.map((feature, index) => (
                    <HStack key={index}>
                      <FiShield color="green" />
                      <Text>{feature}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              <Alert.Root status="warning">
                <Alert.Indicator>
                  <Icon as={FiAlertTriangle} />
                </Alert.Indicator>
                <Alert.Description fontSize="sm">
                  <strong>Test Mode:</strong> This is a test subscription. No payment will be processed.
                </Alert.Description>
              </Alert.Root>

              <Field.Root>
                <Field.Label>Card Number</Field.Label>
                <Input placeholder="1234 1234 1234 1234" disabled />
              </Field.Root>

              <HStack gap={4}>
                <Field.Root>
                  <Field.Label>Expiry Date</Field.Label>
                  <Input placeholder="MM/YY" disabled />
                </Field.Root>
                <Field.Root>
                  <Field.Label>CVC</Field.Label>
                  <Input placeholder="123" disabled />
                </Field.Root>
              </HStack>
            </VStack>
          </Dialog.Body>

          <Dialog.Footer>
            <Button
              colorScheme="brand"
              mr={3}
              onClick={handleSubscribe}
              loading={loading}
            >
              <FiLock /> Subscribe Now - ${planPrice}/month
            </Button>
            <Button
              variant="outline"
              onClick={() => { onCancel(); setIsOpen(false); }}
              disabled={loading}
            >
              Cancel
            </Button>
          </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
};

export default StripePayment; 
